// backend/src/frontend/src/theme.ts
import { createTheme } from '@mui/material/styles';

export const tokens = {
  canvas: '#fdfcfc',
  ink: '#201d1d',
  inkDeep: '#0f0000',
  charcoal: '#302c2c',
  body: '#424245',
  mute: '#646262',
  stone: '#6e6e73',
  ash: '#9a9898',
  surfaceSoft: '#f8f7f7',
  surfaceCard: '#f1eeee',
  surfaceDark: '#201d1d',
  surfaceDarkElevated: '#302c2c',
  hairline: 'rgba(15,0,0,0.12)',
  hairlineStrong: '#646262',
  onDark: '#fdfcfc',
  onDarkMute: '#9a9898',
  accent: '#007aff',
  warning: '#ff9f0a',
  danger: '#ff3b30',
  success: '#30d158',
};

export const monoStack =
  "'JetBrains Mono', 'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace";

const theme = createTheme({
  palette: {
    mode: 'light',
    background: { default: tokens.canvas, paper: tokens.canvas },
    primary: { main: tokens.ink, contrastText: tokens.canvas },
    secondary: { main: tokens.mute },
    error: { main: tokens.danger },
    warning: { main: tokens.warning },
    success: { main: tokens.success },
    info: { main: tokens.accent },
    text: { primary: tokens.ink, secondary: tokens.mute, disabled: tokens.ash },
    divider: tokens.hairline,
  },
  typography: {
    fontFamily: monoStack,
    fontSize: 14,
    button: { fontWeight: 500, lineHeight: 2, textTransform: 'none' },
    h4: { fontWeight: 700 },
    h5: { fontSize: 16, fontWeight: 700 },
    h6: { fontSize: 16, fontWeight: 700 },
    caption: { fontSize: 14, lineHeight: 2 },
  },
  shape: { borderRadius: 4 },
  shadows: Array(25).fill('none') as any,
  components: {
    MuiPaper: { styleOverrides: { root: { boxShadow: 'none', backgroundImage: 'none' } } },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.canvas,
          color: tokens.ink,
          boxShadow: 'none',
          borderBottom: `1px solid ${tokens.hairline}`,
          minHeight: 56,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 4, boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
        containedPrimary: {
          backgroundColor: tokens.ink,
          color: tokens.canvas,
          '&:active': { backgroundColor: tokens.inkDeep },
          '&:hover': { backgroundColor: tokens.charcoal },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.canvas,
          boxShadow: 'none',
          border: 'none',
          borderBottom: `1px solid ${tokens.hairline}`,
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 0 },
        },
      },
    },
    MuiTextField: { defaultProps: { variant: 'outlined' } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.surfaceSoft,
          borderRadius: 4,
          fontFamily: monoStack,
          '& fieldset': { borderColor: tokens.hairline },
          '&:hover fieldset': { borderColor: tokens.hairlineStrong },
          '&.Mui-focused': { backgroundColor: tokens.canvas },
          '&.Mui-focused fieldset': { borderColor: tokens.ink, borderWidth: 1 },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { border: `1px solid ${tokens.hairline}`, borderRadius: 0 },
      },
    },
    MuiFab: { styleOverrides: { root: { boxShadow: 'none', borderRadius: 4 } } },
  },
});

export default theme;
